import { Check, X, ArrowRight, Wallet, TrendingUp, Users, HeartHandshake, Clock, Gauge, Megaphone, Footprints, Sparkles, Briefcase, Target } from "lucide-react";
import { Price, CtaButton, GuideMockup } from "./parts";

const Wrap = ({ children, className = "" }) => <div className={`max-w-6xl mx-auto px-5 sm:px-8 ${className}`}>{children}</div>;
export { Wrap };

export function Hero({ t, offer, cta, idea, heroImg }) {
  const h = t.hero;
  return (
    <section className="relative overflow-hidden bg-white" data-testid="landing-hero">
      <div className="absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full bg-[#EAF8F4]" />
      <Wrap className="relative pt-8 pb-14 md:pt-16 md:pb-24 grid md:grid-cols-[1.15fr_1fr] gap-10 md:gap-8 items-center">
        <div className="fade-up">
          <div className="lp-eyebrow text-[#088F7E]" data-testid="hero-eyebrow">{h.eyebrow}</div>
          <h1 className="font-display font-extrabold text-[#102B3F] text-[34px] leading-[1.06] sm:text-5xl lg:text-6xl mt-4" data-testid="hero-headline">
            {h.h1a}<span className="text-[#0BAF9A]">{h.h1b}</span>
          </h1>
          <p className="text-[#4A5D6A] mt-5 text-[15px] sm:text-lg leading-relaxed max-w-xl">{h.sub}</p>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5 mt-6 max-w-md">
            {h.bullets.map((b) => <li key={b} className="flex items-center gap-2 text-sm font-semibold text-[#102B3F]"><span className="h-5 w-5 rounded-full bg-[#0BAF9A] flex items-center justify-center shrink-0"><Check className="h-3 w-3 text-white" strokeWidth={3} /></span>{b}</li>)}
          </ul>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <CtaButton onClick={() => cta("hero")} testid="landing-hero-cta" className="w-full sm:w-auto">{h.cta} <ArrowRight className="h-5 w-5" /></CtaButton>
            <Price offer={offer} t={t} testid="hero-price" />
          </div>
          <p className="text-xs text-[#7D8C96] mt-3">{h.reassure}</p>
        </div>
        <div className="py-6 md:py-0 stagger">{heroImg === "loading" ? <div className="w-full aspect-[4/3]" /> : heroImg ? <img src={heroImg} alt="Sidebizz" width="1200" height="900" fetchpriority="high" className="w-full rounded-3xl object-cover shadow-[0_24px_60px_-30px_rgba(16,43,63,.55)]" data-testid="hero-custom-image" /> : <GuideMockup t={t} idea={idea} testid="hero-mockup" />}</div>
      </Wrap>
    </section>
  );
}

export function Problem({ t }) {
  const p = t.problem;
  return (
    <section className="bg-[#EAF8F4]" data-testid="landing-problem">
      <Wrap className="py-16 md:py-24 max-w-3xl">
        <h2 className="lp-h2">{p.h}</h2>
        <div className="mt-6 space-y-3 text-[16px] sm:text-lg text-[#36495A] leading-relaxed">{p.p.map((x) => <p key={x}>{x}</p>)}</div>
        <p className="mt-8 border-l-4 border-[#0BAF9A] pl-5 font-display font-bold text-xl sm:text-2xl text-[#102B3F] leading-snug" data-testid="problem-highlight">{p.hl}</p>
      </Wrap>
    </section>
  );
}

export function Different({ t }) {
  const d = t.diff;
  return (
    <section className="bg-white" data-testid="landing-different">
      <Wrap className="py-16 md:py-24">
        <h2 className="lp-h2 max-w-3xl">{d.h}</h2>
        <p className="text-[#4A5D6A] mt-4 text-base sm:text-lg max-w-2xl">{d.p}</p>
        <div className="grid md:grid-cols-[0.85fr_1.15fr] gap-5 mt-10 items-stretch">
          <div className="rounded-3xl bg-[#F4F6F7] border border-dashed border-[#C9D3D9] p-6 sm:p-7" data-testid="compare-random-card">
            <div className="text-xs font-bold tracking-widest text-[#7D8C96] uppercase">{d.leftT}</div>
            <div className="font-display font-bold text-xl text-[#5C6B73] mt-2">{d.leftEx}</div>
            <ul className="mt-5 space-y-2.5">{d.left.map((x) => <li key={x} className="flex gap-2.5 text-sm text-[#5C6B73]"><X className="h-4 w-4 text-[#C2504A] mt-0.5 shrink-0" />{x}</li>)}</ul>
          </div>
          <div className="relative rounded-3xl bg-[#102B3F] p-6 sm:p-8 text-white shadow-[0_30px_60px_-30px_rgba(16,43,63,.7)] md:-my-3" data-testid="compare-sidebizz-card">
            <div className="absolute top-5 right-5 rounded-full bg-[#0BAF9A] px-3 py-1 text-[11px] font-bold">Sidebizz</div>
            <div className="text-xs font-bold tracking-widest text-[#5FE0CB] uppercase">{d.rightT}</div>
            <div className="font-display font-extrabold text-2xl mt-2">{d.rightEx}</div>
            <ul className="mt-5 grid sm:grid-cols-2 gap-x-5 gap-y-2.5">{d.right.map((x) => <li key={x} className="flex gap-2.5 text-[15px]"><Check className="h-4 w-4 text-[#0BAF9A] mt-0.5 shrink-0" strokeWidth={3} />{x}</li>)}</ul>
          </div>
        </div>
      </Wrap>
    </section>
  );
}

const INSIDE_ICONS = [Wallet, TrendingUp, Users, HeartHandshake, Clock, Gauge, Megaphone, Footprints];
export function Inside({ t }) {
  return (
    <section className="bg-[#EAF8F4]" data-testid="landing-inside">
      <Wrap className="py-16 md:py-24">
        <h2 className="lp-h2 max-w-2xl">{t.inside.h}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-9">
          {t.inside.items.map(([title, desc], i) => {
            const Icon = INSIDE_ICONS[i];
            return (
              <div key={title} className="lp-card p-4 sm:p-5 transition-transform duration-200 hover:-translate-y-1" data-testid={`inside-card-${i}`}>
                <div className="h-10 w-10 rounded-xl bg-[#102B3F] flex items-center justify-center"><Icon className="h-5 w-5 text-[#5FE0CB]" /></div>
                <div className="font-display font-bold text-[#102B3F] mt-3 text-[15px] sm:text-base leading-tight">{title}</div>
                <p className="text-[13px] sm:text-sm text-[#5C6B73] mt-1 leading-snug">{desc}</p>
              </div>
            );
          })}
        </div>
      </Wrap>
    </section>
  );
}

const Field = ({ label, children }) => (
  <div className="py-3 border-t border-[#E9EFF1] first:border-0 first:pt-0">
    <div className="text-[11px] font-bold tracking-wider uppercase text-[#088F7E]">{label}</div>
    <div className="text-[14px] text-[#102B3F] mt-1 leading-snug">{children}</div>
  </div>
);
const Bullets = ({ items }) => <ul className="space-y-1">{items.map((x) => <li key={x} className="flex gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#0BAF9A] mt-[7px] shrink-0" />{x}</li>)}</ul>;
const Steps = ({ items }) => <ol className="space-y-1.5">{items.map((x, i) => <li key={x} className="flex gap-2"><span className="h-5 w-5 rounded-full bg-[#102B3F] text-white text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>{x}</li>)}</ol>;

function Revenue({ L, idea, basis }) {
  if (!idea?.revenue_monthly_text) return null;
  return (
    <div className="mt-4 rounded-2xl bg-[#EAF8F4] p-4" data-testid={`preview-revenue-${idea.number}`}>
      <div className="text-[11px] font-bold tracking-wider uppercase text-[#088F7E]">{L.revenue}</div>
      <div className="font-display font-extrabold text-lg text-[#102B3F] mt-0.5">{idea.revenue_monthly_text} <span className="text-xs font-semibold text-[#5C6B73]">{L.perMonth}</span></div>
      <div className="text-[12px] text-[#4A5D6A] mt-1"><b>{L.basis}:</b> {basis}</div>
      <div className="text-[11px] text-[#7D8C96] mt-1">{L.revNote}</div>
    </div>
  );
}

function ExampleCard({ ex, idea, L, kind }) {
  return (
    <article className="lp-card p-5 sm:p-6 flex flex-col" data-testid={`preview-card-${kind}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="rounded-full bg-[#102B3F] text-white text-[11px] font-bold px-3 py-1">{ex.cat}</span>
        {idea && <span className="text-[11px] text-[#7D8C96] font-semibold">#{idea.number}</span>}
      </div>
      <h3 className="font-display font-extrabold text-xl text-[#102B3F] mt-3 leading-tight">{ex.title}</h3>
      {ex.sub && <div className="text-sm text-[#5C6B73]">{ex.sub}</div>}
      <div className="grid grid-cols-2 gap-2 mt-4">
        <div className="rounded-xl bg-[#F4F8F9] p-2.5"><div className="text-[10px] font-bold uppercase text-[#7D8C96]">{kind === "resale" ? L.stock : L.startup}</div><div className="font-bold text-sm text-[#102B3F]">{idea?.startup_cost || "—"}</div></div>
        <div className="rounded-xl bg-[#F4F8F9] p-2.5"><div className="text-[10px] font-bold uppercase text-[#7D8C96]">{L.difficulty}</div><div className="font-bold text-sm text-[#102B3F]">{idea?.difficulty || "—"}</div></div>
      </div>
      <div className="mt-4 flex-1">
        <Field label={L.problem}>{ex.problem}</Field>
        {ex.services && <Field label={L.services}><Bullets items={ex.services} /></Field>}
        <Field label={L.targets}><Bullets items={ex.targets} /></Field>
        {ex.pays && <Field label={L.pays}>{ex.pays}</Field>}
        {ex.find && <Field label={L.find}>{ex.find}</Field>}
        {ex.test && <Field label={L.test}>{ex.test}</Field>}
        {ex.margin && <Field label={L.margin}>{ex.margin}</Field>}
        {ex.equipment && <Field label={L.equipment}>{ex.equipment}</Field>}
        {ex.model && <Field label={L.model}>{ex.model}</Field>}
        {idea?.time_required && <Field label={L.time}>{idea.time_required}</Field>}
        <Field label={L.steps}><Steps items={ex.steps} /></Field>
      </div>
      <Revenue L={L} idea={idea} basis={ex.basis} />
    </article>
  );
}

export function Preview({ t, ideas, cta }) {
  const p = t.preview;
  const kinds = ["service", "resale", "b2b"];
  return (
    <section className="bg-white" data-testid="landing-preview">
      <Wrap className="py-16 md:py-24">
        <h2 className="lp-h2 max-w-3xl">{p.h}</h2>
        <p className="text-[#4A5D6A] mt-4 text-base sm:text-lg max-w-2xl">{p.p}</p>
        <div className="grid lg:grid-cols-3 gap-5 mt-10 items-start">
          {p.ex.map((ex, i) => <ExampleCard key={ex.title} ex={ex} idea={ideas[i]} L={p.labels} kind={kinds[i]} />)}
        </div>
        <div className="mt-10 rounded-3xl bg-[#102B3F] p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-5 justify-between">
          <p className="font-display font-bold text-white text-lg sm:text-xl flex items-center gap-3"><Sparkles className="h-6 w-6 text-[#5FE0CB] shrink-0" />{p.more}</p>
          <CtaButton onClick={() => cta("preview")} testid="landing-preview-cta" className="w-full md:w-auto shrink-0">{p.cta} <ArrowRight className="h-5 w-5" /></CtaButton>
        </div>
      </Wrap>
    </section>
  );
}

export const FIT_ICONS = [Wallet, Clock, Briefcase, Target];
